import { Link } from "react-router-dom";
import { Lock, Crown, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { useSubscription } from "../contexts/SubscriptionProvider";
import { useI18n } from "../i18n/I18nProvider";
import { useAuth } from "../App";

const TIER_REQUIREMENTS = {
  multi_tenant: { tier: "professional", icon: Sparkles },
  ai_policy_generator: { tier: "professional", icon: Sparkles },
  ai_risk_predictor: { tier: "professional", icon: Sparkles },
  ai_implementation_wizard: { tier: "professional", icon: Sparkles },
  evidence_cloud_connectors: { tier: "professional", icon: Sparkles },
  executive_report: { tier: "professional", icon: Sparkles },
  vendor_risk: { tier: "professional", icon: Sparkles },
  threat_intelligence: { tier: "professional", icon: Sparkles },
  smtp_notifications: { tier: "professional", icon: Sparkles },
  bulk_import_export: { tier: "professional", icon: Sparkles },
  rbac_custom_roles: { tier: "enterprise", icon: Crown },
  api_access: { tier: "enterprise", icon: Crown },
  priority_support: { tier: "enterprise", icon: Crown }
};

/**
 * Wrap a premium feature. If tier allows it, children render. Otherwise shows upgrade CTA.
 *
 * Props:
 *   - feature: one of the feature flag keys (e.g., "ai_policy_generator")
 *   - children: the gated content
 *   - fallback: optional custom fallback (by default shows upgrade card)
 */
const FeatureGate = ({ feature, children, fallback }) => {
  const { hasFeature, subscription, loading } = useSubscription();
  const { lang } = useI18n();
  const { user } = useAuth();

  if (user?.is_super_admin) return children;

  // While the subscription is loading, render a neutral skeleton instead of
  // the gated content -- prevents premium-feature bypass before flags arrive.
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[calc(100vh-8rem)]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (hasFeature(feature)) return children;

  if (fallback) return fallback;

  const req = TIER_REQUIREMENTS[feature] || { tier: "professional", icon: Sparkles };
  const Icon = req.icon;
  const tierName = req.tier.charAt(0).toUpperCase() + req.tier.slice(1);
  const currentTierName = subscription?.tier?.name || "Starter";

  return (
    <div className="p-6 flex items-center justify-center min-h-[calc(100vh-8rem)]" data-testid={`feature-gate-${feature}`}>
      <Card className="max-w-md border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="pt-8 pb-8 text-center space-y-5">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <div className="space-y-2">
            <Badge variant="secondary" className="mb-2">
              <Icon className="w-3 h-3 mr-1" />{tierName} {lang === "es" ? "requerido" : "required"}
            </Badge>
            <h2 className="text-xl font-heading font-bold">
              {lang === "es" ? "Funcion Premium" : "Premium Feature"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {lang === "es"
                ? `Esta funcion requiere el plan ${tierName} o superior. Tu plan actual es ${currentTierName}.`
                : `This feature requires the ${tierName} plan or higher. Your current plan is ${currentTierName}.`}
            </p>
          </div>
          <Link to="/billing">
            <Button className="w-full" data-testid="upgrade-cta-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              {lang === "es" ? "Ver planes y actualizar" : "View plans & upgrade"}
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeatureGate;
export { TIER_REQUIREMENTS };
